<template>
  <e-dialog :model-value="true" width="100%" no-border-radius no-content-padding @after-leave="onAfterLeave">
    <template v-if="documentToSign" #title>
      <div class="d-flex align-center w-100">
        <div>
          <div class="caption">
            {{ documentToSign.documentName }}
          </div>
          <status-document :edi-type="ediType" :status="signatureStatus" />
        </div>
        <v-spacer />
        <mobile-floating-bar>
          <div class="d-flex justify-end align-center">
            <e-btn variant="secondary" prepend-icon-src="download" size="M" class="mr-4" @click="download">
              Скачать
            </e-btn>
            <e-btn
              v-if="signatureStatus === ESignatureStatus.Signed1C"
              variant="primary"
              size="M"
              class="mr-md-4"
              :loading="isSignLoading"
              :disabled="!!errors.length"
              @click="startSignDocument"
            >
              Подписать
            </e-btn>
          </div>
        </mobile-floating-bar>
      </div>
    </template>

    <template v-if="signatureStatus !== ESignatureStatus.MutuallySigned && errors.length" #secondaryTitle>
      <div>
        <e-alert variant="warning">
          <div class="paragraph-m font-600">Подписание документа недоступно</div>
          <div v-for="(error, index) in errors" :key="index" class="paragraph-s_h text-secondary">
            {{ error }}.
            <span v-if="index === errors.length - 1 && !isShowMore" class="link" @click="toggleShowMore(true)"
              >Подробнее</span
            >
          </div>
          <div v-if="isShowMore" class="paragraph-s_h text-secondary mt-1">
            Убедитесь, что выполнены следующие условия:
            <ol class="ol-list mt-1">
              <li>Установлено программное обеспечение для работы с ЭЦП КриптоПроCSP.</li>
              <li>Данная страница открыта через Яндекс.Браузер.</li>
              <li>Установлен плагин КриптоПро Browser Plug-in.</li>
              <li>Токен с закрытым ключом установлен в usb порт компьютера.</li>
              <li>Сертификаты установлены в личные сертификаты пользователя.</li>
            </ol>

            <div class="link mt-1" @click="toggleShowMore(false)">Скрыть</div>
          </div>
        </e-alert>
      </div>
    </template>

    <pdf-viewer v-if="pdfViewPath" :src="pdfViewPath" />

    <div v-if="$vuetify.display.smAndDown" style="height: 70px" />
  </e-dialog>

  <alert-dialog
    v-model="isSelectSertificatesDialogOpen"
    title="Выберите сертификат для подписания"
    hide-close-button
    max-width="680px"
  >
    <select-menu
      :items="certificatesMenu"
      :loading-index="loadingCertificateIndex"
      @item-click="onSelectCertificate"
      class="sign-document-modal__select-menu"
    >
      <template #item="{ item }">
        <div v-if="item">
          <div>
            {{ item.organization }}
          </div>
          <div class="paragraph-xs font-400">
            <span class="text-secondary">Выдан:</span>
            {{ item.issuerOrganization }}
            <vertical-line class="text-secondary ml-1 mr-2" style="margin-bottom: -2px" />
            <span class="text-secondary">Дата&nbsp;окончания:</span>
            {{ toShortDateFormat(item.validTo) }}
          </div>
        </div>
      </template>
    </select-menu>
  </alert-dialog>

  <error-message
    :model-value="!!singError"
    title="Документ не подписан"
    :subtitle="documentToSign.documentName"
    is-error
    @close="resetSingError"
  >
    <div v-if="singError" class="sign-document-modal__sing-error">
      {{ singError }}

      <copy-to-clipboard :text="singError" />
    </div>

    <div class="paragraph-s_h">
      Убедитесь, что выполнены следующие условия:
      <ol class="ol-list mt-2">
        <li>Установлено программное обеспечение для работы с ЭЦП КриптоПроCSP.</li>
        <li>Данная страница открыта через Яндекс.Браузер.</li>
        <li>Установлен плагин КриптоПро Browser Plug-in.</li>
        <li>Токен с закрытым ключом установлен в usb порт компьютера.</li>
        <li>Сертификаты установлены в личные сертификаты пользователя.</li>
      </ol>
    </div>
  </error-message>
</template>

<script setup lang="ts">
import { onMounted, ref, toRef } from 'vue';
import { useDocumentsStore } from '../store/documents';
import StatusDocument from './StatusDocument.vue';
import CopyToClipboard from '@/core/components/buttons/CopyToClipboard.vue';
import MobileFloatingBar from '@/core/components/common/MobileFloatingBar.vue';
import SelectMenu from '@/core/components/common/SelectMenu.vue';
import VerticalLine from '@/core/components/helpers/VerticalLine.vue';
import PdfViewer from '@/core/components/interface/PdfViewer.vue';
import AlertDialog from '@/core/components/modals/AlertDialog.vue';
import EDialog from '@/core/components/modals/Dialog.vue';
import { EAlert, EBtn } from '@/core/components/ui';
import { useSnackbar } from '@/core/composables/useSnackbar';
import { toShortDateFormat } from '@/core/utils/date';
import { blobToBase64, getBlobFromUrl } from '@/core/utils/file';
import { loadScriptPromise } from '@/core/utils/script';
import { isObject, isString } from '@/core/utils/type-guards';
import { EdiType, ESignatureStatus, type GetDocumentToSignQuery } from '@/graphql/generated';
import ErrorMessage from '@/parts/errors/components/ErrorMessage.vue';

type Sertificate = {
  name: string;
  certObject: unknown;
  subject: string;
  issuer: string;
  validFrom: Date;
  organization: string;
  issuerOrganization: string;
  index: number;
  validTo: Date;
  country: string | null;
};

const props = defineProps<{
  documentToSign: GetDocumentToSignQuery['documentToSign'];
  ediType: EdiType;
  signatureStatus: ESignatureStatus | undefined | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'success'): void;
  (e: 'download'): void;
}>();

const { showSnackbar } = useSnackbar();
const { saveDocumentSignature } = useDocumentsStore();

const pdfViewPath = toRef(() => props.documentToSign?.previewFileUri);

const download = async () => {
  emit('download');
};

const errors = ref<string[]>([]);

const certificatesList = ref<Sertificate[]>([]);

const certificatesMenu = toRef(() =>
  certificatesList.value.map((certificate) => ({
    title: certificate.name,
    ...certificate,
  })),
);

const getCertificatesList = async () =>
  new Promise((resolve, reject) => {
    errors.value = [];

    try {
      window.cadesplugin
        .then(async () => {
          const getCerts = async () => {
            try {
              const store = await window.cadesplugin.CreateObjectAsync('CAdESCOM.Store');

              await store.Open();

              const certs = await store.Certificates;
              const count = await certs.Count;

              const list = [] as Sertificate[];

              for (let i = 1; i <= count; i++) {
                try {
                  const cert = await certs.Item(i);
                  const subject = await cert.SubjectName;
                  const issuer = await cert.IssuerName;
                  const validFrom = new Date(await cert.ValidFromDate);
                  const validTo = new Date(await cert.ValidToDate);

                  const orgMatch = subject.match(/CN=([^,]+)/);
                  const org = orgMatch ? orgMatch[1] : 'Не указано';

                  const issuerMatch = issuer.match(/CN=([^,]+)/);
                  const issuerOrganization = issuerMatch ? issuerMatch[1] : 'Не указано';

                  const countryMatch = subject.match(/C=([^,]+)/);
                  const country = countryMatch ? countryMatch[1] : null;

                  const name = `(${validFrom.toLocaleDateString()}) ${subject} ${org} `;
                  list.push({
                    name,
                    certObject: cert,
                    subject: subject,
                    issuer: issuer,
                    validFrom: validFrom,
                    validTo: validTo,
                    organization: org,
                    issuerOrganization,
                    index: i,
                    country,
                  });
                } catch (e: unknown) {
                  console.error(e);

                  errors.value.push(`Ошибка чтения сертификата ${i}: ${getErrorText(e)}`);

                  reject();
                }
              }

              certificatesList.value = list.sort((a) => (a.country === 'RU' ? -1 : 1));

              console.log('certificatesList', certificatesList.value);
              if (!certificatesList.value.length) {
                errors.value.push('Сертификаты не найдены');

                reject();
              }

              await store.Close();

              resolve(certificatesList.value);
            } catch (error: unknown) {
              console.error(error);
              errors.value.push(`Ошибка: ${getErrorText(error)}`);

              reject(error);
            }
          };

          await getCerts();

          resolve(certificatesList.value);
        })
        .catch((error: Error) => {
          console.error(error);

          errors.value.push(`Ошибка загрузки расширения: ${getErrorText(error)}`);

          reject();
        });
    } catch (error) {
      console.error(error);

      errors.value.push(`Ошибка загрузки расширения: ${getErrorText(error)}`);
      reject();
    }
  });

const isShowMore = ref(false);

const toggleShowMore = (value: boolean) => {
  isShowMore.value = value;
};

const isSelectSertificatesDialogOpen = ref(false);
const isSignLoading = ref(false);

const startSignDocument = async () => {
  isSelectSertificatesDialogOpen.value = true;
};

const sendSignature = async (signature: string) => {
  await saveDocumentSignature(props.documentToSign.documentId, signature);
};

const loadingCertificateIndex = ref<number | null>(null);
const singError = ref<string | null>(null);

const resetSingError = () => {
  singError.value = null;
};

const onSelectCertificate = async (certificate: Sertificate, index: number) => {
  if (!props.documentToSign?.fileUri) {
    throw new Error('PDF path is not defined');
  }

  singError.value = null;

  try {
    loadingCertificateIndex.value = index;

    const cadesplugin = window.cadesplugin;

    const blob = await getBlobFromUrl(props.documentToSign.fileUri);
    const header = ';base64,';
    const fileData = await blobToBase64(blob);
    const base64Content = fileData.substr(fileData.indexOf(header) + header.length);

    const oSigner = await cadesplugin.CreateObjectAsync('CAdESCOM.CPSigner');

    const oSigningTimeAttr = await cadesplugin.CreateObjectAsync('CADESCOM.CPAttribute');
    const CAPICOM_AUTHENTICATED_ATTRIBUTE_SIGNING_TIME = 0;
    await oSigningTimeAttr.propset_Name(CAPICOM_AUTHENTICATED_ATTRIBUTE_SIGNING_TIME);
    const oTimeNow = new Date();
    await oSigningTimeAttr.propset_Value(oTimeNow);
    const attr = await oSigner.AuthenticatedAttributes2;
    await attr.Add(oSigningTimeAttr);

    const oDocumentNameAttr = await cadesplugin.CreateObjectAsync('CADESCOM.CPAttribute');
    const CADESCOM_AUTHENTICATED_ATTRIBUTE_DOCUMENT_NAME = 1;
    await oDocumentNameAttr.propset_Name(CADESCOM_AUTHENTICATED_ATTRIBUTE_DOCUMENT_NAME);
    await oDocumentNameAttr.propset_Value('Document Name');
    await attr.Add(oDocumentNameAttr);

    await oSigner.propset_Certificate(certificate.certObject);

    const oSignedData = await cadesplugin.CreateObjectAsync('CAdESCOM.CadesSignedData');
    const CADES_BES = 1;

    const dataToSign = base64Content;
    if (dataToSign) {
      await oSignedData.propset_ContentEncoding(1);
      await oSignedData.propset_Content(dataToSign);
    }
    await oSigner.propset_Options(cadesplugin.CAPICOM_CERTIFICATE_INCLUDE_END_ENTITY_ONLY);
    const isDetached = true;

    const signature = await oSignedData.SignCades(
      oSigner,
      CADES_BES | cadesplugin.CADES_USE_OCSP_AUTHORIZED_POLICY,
      isDetached,
    );

    await sendSignature(signature);

    showSnackbar({ type: 'success', title: 'Документ подписан', description: props.documentToSign.documentName });

    emit('success');

    isSelectSertificatesDialogOpen.value = false;
  } catch (error) {
    console.error(error);

    singError.value = `Ошибка подписи: ${getErrorText(error)}`;
  } finally {
    loadingCertificateIndex.value = null;
  }
};

const onAfterLeave = () => {
  emit('close');
};

onMounted(async () => {
  if (props.signatureStatus === ESignatureStatus.MutuallySigned) {
    return;
  }

  isSignLoading.value = true;
  try {
    await loadScriptPromise('/js/cadesplugin_api.js');
    await getCertificatesList();
  } finally {
    isSignLoading.value = false;
  }
});

function getErrorText(error: unknown) {
  if (!error) {
    return 'Неизвестная ошибка';
  }

  if (isString(error)) {
    return error;
  }

  if (isObject(error) && 'message' in error) {
    return error.message;
  }

  return 'Неизвестная ошибка';
}
</script>

<style scoped lang="scss">
.sign-document-modal {
  &__select-menu {
    max-height: min(400px, 50vh);
    overflow-y: auto;
  }

  &__sing-error {
    display: flex;
    justify-content: space-between;
    align-items: center;

    margin: 16px 0;
    padding: 8px 16px;

    color: $text-ordinary-color;

    background-color: $bg-light-color;
    border: 1px solid $border-color-light;
    border-radius: $input-border-radius;

    @include input-text();
  }
}
</style>
